import { useState, useEffect } from "react";
import {
  Box, Button, IconButton, InputBase, Typography,
  Modal, TextField, Menu, MenuItem, Snackbar, Alert
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import isBetween from "dayjs/plugin/isBetween";
import { format } from "date-fns";
import ExcelJS from "exceljs";
import { tokens } from "../../../theme";
import Header from "../../../components/Header";
import ModalCharge from "../../../modal/modalCharge";
import { getMarcacionesView, updateMarcacion } from "../../../services/marcaciones.services";

dayjs.extend(duration);
dayjs.extend(isBetween);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Recibe la cadena "2026-04-17 12:35:23.000, 2026-04-17 16:01:03.000"
 * y devuelve un array de strings ISO ordenados.
 */
const parseMarcacionesExtra = (raw) => {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .sort();
};

/**
 * Formatea un string de fecha a "HH:mm:ss", o devuelve "" si es inválido.
 */
const fmtHour = (val) => {
  if (!val) return "";
  try {
    return format(new Date(val), "HH:mm:ss");
  } catch {
    return "";
  }
};

// ---------------------------------------------------------------------------

const ListaMarcacionesExtras = () => {
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Edit modal
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    entrada: "", salida: "", salida_almuerzo: "", entrada_almuerzo: "",
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, message: "", severity: "success" });

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // -------------------------------------------------------------------------
  // Fetch
  // -------------------------------------------------------------------------
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const datos = await getMarcacionesView();
        console.log("🚀 ~ fetchData ~ datos:", datos)
        // Solo nos interesan filas que tengan marcaciones extras
        const conExtras = datos
          .filter((row) => row.extra && row.extra.trim() !== "")
          .map((row) => ({
            ...row,
            _extras: parseMarcacionesExtra(row.extra), // array interno
          }));

        setOriginalData(conExtras);
        setData(conExtras);
      } catch (err) {
        console.error("Error al obtener marcaciones extras:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // -------------------------------------------------------------------------
  // Filtros (fecha + búsqueda)
  // -------------------------------------------------------------------------
  useEffect(() => {
    let filtered = originalData;

    if (startDate && endDate) {
      const endOfDay = dayjs(endDate).endOf("day");
      filtered = filtered.filter((item) => {
        const itemDate = dayjs(item.Fecha);
        return itemDate.isBetween(startDate, endOfDay, null, "[]");
      });
    }

    if (searchValue) {
      filtered = filtered.filter((item) =>
        (item.funcionario || "").toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    setData(filtered);
  }, [startDate, endDate, searchValue, originalData]);

  // -------------------------------------------------------------------------
  // Menú / edición (igual que el componente base)
  // -------------------------------------------------------------------------
  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuRow(row);
  };
  const handleMenuClose = () => setMenuAnchorEl(null);

  const formatTimeField = (value) => (value ? dayjs(value).format("HH:mm") : "");

  const handleEditMarcacion = () => {
    if (!menuRow) return;
    setEditForm({
      entrada: formatTimeField(menuRow.entrada),
      salida: formatTimeField(menuRow.salida),
      salida_almuerzo: formatTimeField(menuRow.salida_almuerzo),
      entrada_almuerzo: formatTimeField(menuRow.entrada_almuerzo),
    });
    setIsEditModalOpen(true);
    handleMenuClose();
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setMenuRow(null);
    setEditForm({ entrada: "", salida: "", salida_almuerzo: "", entrada_almuerzo: "" });
  };

  const handleEditInputChange = (field) => (event) =>
    setEditForm((prev) => ({ ...prev, [field]: event.target.value }));

  const resolveBaseDate = (row) => {
    if (row?.entrada && dayjs(row.entrada).isValid()) return dayjs(row.entrada);
    if (row?.Fecha && dayjs(row.Fecha).isValid()) return dayjs(row.Fecha);
    return dayjs();
  };

  const combineDateAndTime = (row, timeValue, fieldKey) => {
    if (!timeValue) return null;
    const [hours, minutes] = timeValue.split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

    const fechaReferencia = row?.Fecha && dayjs(row.Fecha).isValid() ? dayjs(row.Fecha) : null;
    const campoExistente =
      fieldKey && row?.[fieldKey] && dayjs(row[fieldKey]).isValid()
        ? dayjs(row[fieldKey])
        : null;

    let base = campoExistente || resolveBaseDate(row);
    if (fechaReferencia) {
      base = base
        .year(fechaReferencia.year())
        .month(fechaReferencia.month())
        .date(fechaReferencia.date());
    }

    return base
      .hour(hours)
      .minute(minutes)
      .second(campoExistente ? campoExistente.second() : 0)
      .millisecond(campoExistente ? campoExistente.millisecond() : 0)
      .format("YYYY-MM-DD HH:mm:ss.SSS");
  };

  const handleSaveEdit = async () => {
    if (!menuRow) return;
    setSavingEdit(true);
    try {
      const payload = {
        entrada: combineDateAndTime(menuRow, editForm.entrada, "entrada"),
        salida: combineDateAndTime(menuRow, editForm.salida, "salida"),
        salida_almuerzo: combineDateAndTime(menuRow, editForm.salida_almuerzo, "salida_almuerzo"),
        entrada_almuerzo: combineDateAndTime(menuRow, editForm.entrada_almuerzo, "entrada_almuerzo"),
      };

      await updateMarcacion(menuRow.Id, payload);

      const match = (row) =>
        row.Id === menuRow.Id && row.id_usuario === menuRow.id_usuario;

      setOriginalData((prev) =>
        prev.map((row) => (match(row) ? { ...row, ...payload } : row))
      );
      setData((prev) =>
        prev.map((row) => (match(row) ? { ...row, ...payload } : row))
      );

      setFeedback({ open: true, severity: "success", message: "Marcación actualizada" });
      handleCloseEditModal();
    } catch (error) {
      console.error("Error al actualizar:", error);
      setFeedback({ open: true, severity: "error", message: error.message || "No se pudo actualizar" });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleFeedbackClose = (_, reason) => {
    if (reason === "clickaway") return;
    setFeedback((prev) => ({ ...prev, open: false }));
  };

  // -------------------------------------------------------------------------
  // Exportar Excel
  // -------------------------------------------------------------------------
  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Extras");

    worksheet.columns = [
      { header: "Nombre", key: "nombre", width: 20 },
      { header: "Apellido", key: "apellido", width: 20 },
      { header: "Sector", key: "sector", width: 15 },
      { header: "Entrada", key: "entrada", width: 12 },
      { header: "Salida Almuerzo", key: "salida_almuerzo", width: 14 },
      { header: "Entrada Almuerzo", key: "entrada_almuerzo", width: 14 },
      { header: "Salida", key: "salida", width: 12 },
      { header: "Fecha", key: "fecha", width: 14 },
      { header: "Marcaciones Extra", key: "marcacion_extra", width: 40 },
    ];

    const exportableData = data.map((row) => ({
      nombre: row.nombre || "Sin Registro",
      apellido: row.apellido || "Sin Registro",
      sector: row.grupo || "Sin Registro",
      entrada: fmtHour(row.entrada) || "Sin Registro",
      salida_almuerzo: fmtHour(row.salida_almuerzo) || "0",
      entrada_almuerzo: fmtHour(row.entrada_almuerzo) || "0",
      salida: fmtHour(row.salida) || "0",
      fecha: row.Fecha ? dayjs(row.Fecha).format("DD/MM/YYYY") : "0",
      // Mostramos cada extra ya formateada HH:mm:ss, separadas por " | "
      marcacion_extra: (row._extras || [])
        .map((e) => fmtHour(e))
        .filter(Boolean)
        .join(" | "),
    }));

    worksheet.addRows(exportableData);

    // Estilos de cabecera
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "143f04" } };
      cell.border = {
        top: { style: "thin" }, left: { style: "thin" },
        bottom: { style: "thin" }, right: { style: "thin" },
      };
    });

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      let hasInvalid = false;
      row.eachCell((cell) => {
        if (cell.value === "0") hasInvalid = true;
        cell.border = {
          top: { style: "thin" }, left: { style: "thin" },
          bottom: { style: "thin" }, right: { style: "thin" },
        };
      });
      if (hasInvalid) {
        row.eachCell((cell) => {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0000" } };
          cell.font = { color: { argb: "FFFFFF" } };
        });
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Resumen_Extras.xlsx";
    link.click();
  };

  // -------------------------------------------------------------------------
  // Columnas
  // -------------------------------------------------------------------------
  const columns = [
    {
      field: "acciones",
      headerName: "Acciones",
      sortable: false,
      filterable: false,
      width: 110,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MenuIcon />
        </IconButton>
      ),
    },
    { field: "Id", headerName: "ID", width: 70 },
    { field: "Id_empleado", headerName: "ID_EMPLEADO", width: 110 },
    { field: "funcionario", headerName: "NOMBRE", flex: 1, cellClassName: "name-column--cell" },
    { field: "grupo", headerName: "GRUPO", flex: 1, cellClassName: "name-column--cell" },
    { field: "horas", headerName: "HORAS TRABAJADAS", flex: 1 },
    {
      field: "entrada",
      headerName: "Entrada",
      flex: 1,
      renderCell: (params) => fmtHour(params.value) || "0",
    },
    {
      field: "salida_almuerzo",
      headerName: "Salida Almuerzo",
      flex: 1,
      renderCell: (params) => fmtHour(params.value) || "0",
    },
    {
      field: "entrada_almuerzo",
      headerName: "Entrada Almuerzo",
      flex: 1,
      renderCell: (params) => fmtHour(params.value) || "0",
    },
    {
      field: "salida",
      headerName: "Salida",
      flex: 1,
      renderCell: (params) => fmtHour(params.value) || "0",
    },
    {
      field: "Fecha",
      headerName: "FECHA",
      flex: 1,
      renderCell: (params) =>
        params.value ? format(new Date(params.value), "dd/MM/yyyy") : "",
    },
    {
      // Campo nuevo: muestra las marcaciones extras separadas por " | "
      field: "marcacion_extra",
      headerName: "Marcaciones Extra",
      flex: 2,
      renderCell: (params) => {
        const extras = params.row._extras || [];
        if (!extras.length) return "";
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", py: "4px" }}>
            {extras.map((e, i) => (
              <Box
                key={i}
                sx={{
                  backgroundColor: "#143f04",
                  color: "#fff",
                  borderRadius: "4px",
                  px: "6px",
                  py: "2px",
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                }}
              >
                {fmtHour(e)}
              </Box>
            ))}
          </Box>
        );
      },
    },
  ];

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" p={0}>
        <Header
          title="Resumen de Asistencias (Extras)"
          subtitle="Funcionarios con marcaciones adicionales"
        />
        <Box display="flex" alignItems="center">
          <Box sx={{ marginRight: "2%", marginLeft: "2%" }}>
            <Button
              sx={{
                background: "#143f04",
                width: "100%",
                height: "3rem!important",
                color: "white",
                borderRadius: "8px",
                "&:hover": { background: "#36a60f" },
              }}
              onClick={handleExport}
              variant="contained"
            >
              <img
                src="https://cdn-icons-png.flaticon.com/512/732/732220.png"
                alt="Excel Icon"
                style={{ width: 24, height: 24, marginRight: 8 }}
              />
              Exportar
            </Button>
          </Box>
          <Box
            display="flex"
            backgroundColor={colors.primary[400]}
            borderRadius="3px"
            height="50%"
            width="100%"
            ml={1}
          >
            <InputBase
              sx={{ ml: 2, flex: 1 }}
              placeholder="Buscar"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
            <IconButton type="button" sx={{ p: 1 }}>
              <SearchIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .name-column--cell": { color: colors.greenAccent[300] },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[900],
            borderBottom: "0.5px solid #5f5d5d",
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[900],
          },
          "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
          "& .fila-invalida": {
            backgroundColor: "#740017",
            color: "#FFFFFF",
            "&:hover": { backgroundColor: "#CC0000" },
          },
        }}
      >
        {/* Filtros de fecha */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "-1%", mr: "5px" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DatePicker"]}>
              <DatePicker
                label="Fecha de Inicio"
                value={startDate}
                onChange={(v) => setStartDate(dayjs(v))}
                format="DD/MM/YYYY"
              />
              <DatePicker
                label="Fecha de Fin"
                value={endDate}
                onChange={(v) => setEndDate(dayjs(v))}
                format="DD/MM/YYYY"
              />
            </DemoContainer>
          </LocalizationProvider>
        </Box>

        <DataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => `${row.Id}_${row.id_usuario}`}
          getRowClassName={(params) =>
            !params.row.salida || params.row.salida === "0" ? "fila-invalida" : ""
          }
          components={{ Toolbar: GridToolbar }}
        />

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <MenuItem onClick={handleEditMarcacion}>Editar marcación</MenuItem>
        </Menu>
      </Box>

      {/* Modal de edición */}
      <Modal open={isEditModalOpen} onClose={handleCloseEditModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            maxWidth: 520,
            bgcolor: colors.primary[400],
            borderRadius: "8px",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">Editar marcación</Typography>
            <IconButton onClick={handleCloseEditModal} sx={{ color: colors.grey[400] }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={2} mb={3}>
            <TextField
              label="Entrada" type="time" value={editForm.entrada}
              onChange={handleEditInputChange("entrada")}
              InputLabelProps={{ shrink: true }} fullWidth
            />
            <TextField
              label="Salida almuerzo" type="time" value={editForm.salida_almuerzo}
              onChange={handleEditInputChange("salida_almuerzo")}
              InputLabelProps={{ shrink: true }} fullWidth
            />
            <TextField
              label="Entrada almuerzo" type="time" value={editForm.entrada_almuerzo}
              onChange={handleEditInputChange("entrada_almuerzo")}
              InputLabelProps={{ shrink: true }} fullWidth
            />
            <TextField
              label="Salida" type="time" value={editForm.salida}
              onChange={handleEditInputChange("salida")}
              InputLabelProps={{ shrink: true }} fullWidth
            />
          </Box>

          <Box bgcolor={colors.blueAccent[800]} borderRadius="4px" p={2} mb={3}>
            <Typography variant="body2" color={colors.grey[100]}>
              Ajusta los horarios según corresponda. Los cambios quedan registrados en el historial una vez confirmes.
            </Typography>
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button onClick={handleCloseEditModal} disabled={savingEdit} color="secondary">
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} variant="contained" disabled={savingEdit}>
              {savingEdit ? "Guardando..." : "Guardar"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <ModalCharge isLoading={isLoading} />
      <Snackbar open={feedback.open} autoHideDuration={4000} onClose={handleFeedbackClose}>
        <Alert onClose={handleFeedbackClose} severity={feedback.severity} sx={{ width: "100%" }}>
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ListaMarcacionesExtras;
